import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useToastStore } from "&/toast";
import { useHistoryBackGuard } from "^/common/useHistoryBackGuard";

export const Route = createFileRoute("/customer/layout/payment/fail")({
  component: PaymentFail,
});

function PaymentFail() {
  const { add } = useToastStore();
  const navigate = useNavigate();
  const [errorInfo, setErrorInfo] = useState<any>(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");
    const message = urlParams.get("message");
    const orderId = urlParams.get("orderId");

    // 🔥 PAY_PROCESS_CANCELED 시 orderId 복구 (세션 스토리지에서)
    let finalOrderId = orderId;
    let savedPaymentData = null;
    
    if (code === "PAY_PROCESS_CANCELED") {
      const savedOrderId = sessionStorage.getItem('currentPaymentOrderId');
      const savedData = sessionStorage.getItem('currentPaymentData');
      
      if (savedOrderId) {
        finalOrderId = savedOrderId;
      }
      
      if (savedData) {
        try {
          savedPaymentData = JSON.parse(savedData);
        } catch (error) {
          console.error("저장된 결제 정보 파싱 실패:", error);
        }
      }
      
      // 🔥 결제 취소 플래그 설정 (payment/index.tsx에서 상태 해제용)
      sessionStorage.setItem('paymentCanceled', 'true');
    }

    setErrorInfo({
      code,
      message,
      orderId: finalOrderId,
      savedPaymentData,
      isCanceled: code === "PAY_PROCESS_CANCELED",
    });

    // 에러 메시지 표시
    if (message) {
      add(message);
    } else if (code) {
      if (code === "PAY_PROCESS_CANCELED") {
        add("결제가 취소되었습니다. 언제든지 다시 시도하실 수 있습니다.");
      } else {
        add(`결제 실패: ${code}`);
      }
    } else {
      add("결제에 실패했습니다.");
    }
  }, [add]);

  const getErrorMessage = (code: string) => {
    switch (code) {
      case "PAY_PROCESS_CANCELED":
        return "결제가 취소되었습니다. 언제든지 다시 시도하실 수 있습니다.";
      case "PAY_PROCESS_ABORTED":
        return "결제가 중단되었습니다.";
      case "REJECT_CARD_COMPANY":
        return "카드사에서 결제를 거절했습니다.";
      case "NOT_FOUND_PAYMENT_SESSION":
        return "결제 시간이 만료되었습니다.";
      case "FORBIDDEN_REQUEST":
        return "잘못된 결제 요청입니다.";
      case "UNAUTHORIZED_KEY":
        return "인증에 실패했습니다.";
      default:
        return "결제에 실패했습니다.";
    }
  };

  const handleRetry = () => {
    // 🔥 저장된 결제 정보가 있으면 복원하여 재시도
    if (errorInfo?.savedPaymentData) {
      navigate({
        to: "/customer/layout/payment",
        search: {
          paymentData: encodeURIComponent(JSON.stringify(errorInfo.savedPaymentData)),
        },
      });
    } else {
      // 저장된 정보가 없으면 일반 결제 페이지로
      navigate({ to: "/customer/layout/payment" });
    }
  };

  const handleGoHome = () => {
    navigate({ to: "/customer/layout/home" });
  };

  // 🔥 X 버튼 클릭 시 이전 페이지로 이동 (하이브리드 방식)
  const handleGoBack = () => {
    // 1순위: 저장된 결제 정보에서 이전 페이지 추론
    const savedPaymentData = sessionStorage.getItem('currentPaymentData');
    if (savedPaymentData) {
      try {
        const data = JSON.parse(savedPaymentData);
        if (data.companySeq) {
          // 이용권 구매 페이지로 이동
          navigate({
            to: "/customer/layout/subscription-list",
            // 문자열로 넘기면 JSON 직렬화로 URL이 %22..%22 가 되므로 숫자로 전달
            search: { companySeq: Number(data.companySeq) as unknown as string },
          });
          return;
        }
      } catch (error) {
        console.error("결제 정보 파싱 실패:", error);
      }
    }
    
    // 2순위: 홈으로
    // (브라우저 back 은 만료된 토스 결제창으로 되돌아가므로 사용하지 않는다)
    navigate({ to: "/customer/layout/home" });
  };

  // 이 화면의 직전 히스토리 항목은 토스 결제창(외부 도메인)이다.
  // 그대로 두면 뒤로가기 시 "결제할 수 있는 시간이 지났어요" 만료 화면으로 빠져
  // 앱으로 되돌아올 방법이 없어지므로, 뒤로가기를 앱 화면으로 돌린다.
  useHistoryBackGuard(handleGoBack);

  return (
    <div className="flex justify-center items-center flex-auto bg-gray-50">
      <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
        <div className="text-center">
          {/* 🔥 실패 아이콘 (클릭 가능한 X 버튼) */}
          <div className="mb-6">
            <button
              onClick={handleGoBack}
              className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 hover:bg-red-200 transition-colors cursor-pointer"
              aria-label="닫기"
            >
              <svg
                className="h-8 w-8 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* 실패 메시지 */}
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            결제에 실패했습니다
          </h1>
          <p className="text-gray-600 mb-6">
            {errorInfo?.code
              ? getErrorMessage(errorInfo.code)
              : "결제 처리 중 오류가 발생했습니다."}
          </p>

          {/* 에러 정보 */}
          {errorInfo && (
            <div className="bg-red-50 p-4 rounded-lg mb-6 text-left">
              <h3 className="font-semibold text-red-900 mb-2">오류 정보</h3>
              <div className="space-y-1 text-sm text-red-700">
                {errorInfo.code && <div>오류 코드: {errorInfo.code}</div>}
                {errorInfo.message && (
                  <div>오류 메시지: {errorInfo.message}</div>
                )}
                {errorInfo.orderId && <div>주문번호: {errorInfo.orderId}</div>}
              </div>
            </div>
          )}

          {/* 안내 메시지 */}
          <div className="bg-blue-50 p-4 rounded-lg mb-6 text-left">
            <h3 className="font-semibold text-blue-900 mb-2">안내사항</h3>
            <div className="text-sm text-blue-700 space-y-1">
              {errorInfo?.isCanceled ? (
                <>
                  <div>• 결제가 취소되었습니다. 언제든지 다시 시도하실 수 있습니다.</div>
                  <div>• "다시 시도하기" 버튼을 누르면 이전 결제 정보로 자동 복원됩니다.</div>
                  <div>• 문제가 지속되면 고객센터에 문의해주세요.</div>
                </>
              ) : (
                <>
                  <div>• 결제 정보를 다시 확인해주세요.</div>
                  <div>• 카드 한도나 잔액을 확인해주세요.</div>
                  <div>• 문제가 지속되면 고객센터에 문의해주세요.</div>
                </>
              )}
            </div>
          </div>

          {/* 버튼 */}
          <div className="space-y-3">
            <button
              onClick={handleRetry}
              className="w-full bg-orange-500 text-white py-3 px-4 rounded-lg font-medium hover:bg-orange-600 transition-colors"
            >
              다시 시도하기
            </button>
            <button
              onClick={handleGoHome}
              className="w-full bg-gray-200 text-gray-800 py-3 px-4 rounded-lg font-medium hover:bg-gray-300 transition-colors"
            >
              홈으로 이동
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
